import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import WeekDatumPicker from '../components/WeekDatumPicker';

const S = {
  input:  { padding: '8px 10px', border: '1px solid #DDD', borderRadius: 6, fontSize: 13, width: '100%', boxSizing: 'border-box' },
  fout:   { padding: '8px 10px', border: '1px solid #E05040', borderRadius: 6, fontSize: 13, width: '100%', boxSizing: 'border-box' },
  label:  { fontSize: 12, color: '#555', display: 'block', marginBottom: 4 },
  kaart:  { background: '#fff', border: '1px solid #E0E0E0', borderRadius: 10, padding: 20, marginBottom: 14 },
  secHdr: { fontWeight: 600, fontSize: 14, color: '#1F3864', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 },
};

const MAT_STATUS = { in_stock: 'In stock', te_bestellen: 'Te bestellen', besteld: 'Besteld', geleverd: 'Geleverd' };
const leegMat = () => ({ _id: Math.random(), naam: '', referentie: '', leverancier_id: '', eenheid: 'stuks', tanden_per_stuk: '', vereist_voor_operaties: [], status: 'te_bestellen', opmerkingen: '' });
const vandaag = () => new Date().toISOString().substring(0, 10);

function SecNr({ n }) {
  return <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#185FA5', color: '#fff', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{n}</div>;
}

function Veld({ label, fout, children }) {
  return (
    <div>
      <label style={{ ...S.label, color: fout ? '#C0392B' : '#555' }}>{label}{fout ? ` — ${fout}` : ''}</label>
      {children}
    </div>
  );
}

export default function NieuwOrderPage() {
  const nav = useNavigate();
  const [artikelen, setArtikelen]   = useState([]);
  const [fiches, setFiches]         = useState([]);
  const [families, setFamilies]     = useState([]);
  const [operaties, setOperaties]   = useState([]);
  const [leveranciers, setLeveranciers] = useState([]);
  const [geselecteerdArtikel, setGeselecteerdArtikel] = useState(null);
  const [geselecteerdeFiche, setGeselecteerdeFiche] = useState(null);
  const [route, setRoute]           = useState([]);
  const [materialen, setMaterialen] = useState([]);
  const [templateBericht, setTemplateBericht] = useState('');
  const [artikelZoek, setArtikelZoek] = useState('');
  const [familieFilter, setFamilieFilter] = useState('');
  const [ficheZoek, setFicheZoek]   = useState('');
  const [fouten, setFouten]         = useState({});
  const [bezig, setBezig]           = useState(false);
  const [bewaarAlsArtikel, setBewaarAlsArtikel] = useState(false);
  const [klantArtikelen, setKlantArtikelen] = useState([]);
  const [klantArtikelenLaden, setKlantArtikelenLaden] = useState(false);
  const [materiaalSuggesties, setMateriaalSuggesties] = useState([]);

  const [form, setForm] = useState({
    ordernummer: '',
    klant: '', klant_referentie: '', artikel_id: '', productfiche_id: '',
    artikel_omschrijving: '', aantal: 1,
    startdatum: vandaag(), leverdatum: '', prioriteit: 'normaal',
    tekeningnummer: '', diameter_mm: '', tanden_per_stuk: '',
    materiaal_beschrijving: '', materiaal_referentie: '',
    materiaal_leverancier_id: '', materiaal_status: 'in_stock',
    uitbesteding_info: '', opmerkingen: '',
  });

  const stel = (veld, val) => {
    setForm(f => ({ ...f, [veld]: val }));
    if (fouten[veld]) setFouten(e => { const n = { ...e }; delete n[veld]; return n; });
  };

  useEffect(() => {
    Promise.all([
      api.get('/artikelen'),
      api.get('/productfiches'),
      api.get('/productfamilies'),
      api.get('/operaties'),
      api.get('/leveranciers'),
    ]).then(([a, f, fam, o, l]) => {
      setArtikelen(a.data.filter(x => x.actief !== false));
      setFiches(f.data);
      setFamilies(fam.data.filter(f => f.actief !== false));
      setOperaties(o.data);
      setLeveranciers(l.data);
    });
  }, []);

  // Haal historische artikelen op voor ingetypte klant (debounced)
  useEffect(() => {
    const klant = form.klant.trim();
    if (!klant) { setKlantArtikelen([]); return; }
    const timer = setTimeout(async () => {
      setKlantArtikelenLaden(true);
      try {
        const { data } = await api.get(`/orders/klant-artikelen?klant=${encodeURIComponent(klant)}`);
        setKlantArtikelen(data);
      } catch { setKlantArtikelen([]); }
      setKlantArtikelenLaden(false);
    }, 500);
    return () => clearTimeout(timer);
  }, [form.klant]);

  const gefilterdArtikel = useMemo(() => artikelen.filter(a => {
    if (familieFilter && a.familie_naam !== families.find(f => String(f.id) === familieFilter)?.naam) return false;
    if (artikelZoek) {
      const q = artikelZoek.toLowerCase();
      return `${a.artikelnummer || ''} ${a.naam} ${a.familie_naam || ''}`.toLowerCase().includes(q);
    }
    return true;
  }), [artikelen, familieFilter, artikelZoek, families]);

  const gefilterdeFiches = useMemo(() => fiches.filter(f => {
    if (familieFilter && String(f.productfamilie_id) !== familieFilter) return false;
    if (ficheZoek) {
      const q = ficheZoek.toLowerCase();
      return `${f.artikelnummer || ''} ${f.omschrijving}`.toLowerCase().includes(q);
    }
    return true;
  }), [fiches, familieFilter, ficheZoek]);

  const laadArtikel = async (id) => {
    stel('artikel_id', id);
    setMateriaalSuggesties([]);
    if (!id) {
      setGeselecteerdArtikel(null);
      setRoute([]); setMaterialen([]); setTemplateBericht('');
      return;
    }
    const { data: artikel } = await api.get(`/artikelen/${id}`);
    setGeselecteerdArtikel(artikel);
    setRoute(artikel.route || []);
    setForm(f => ({
      ...f,
      artikel_id: id,
      productfiche_id: artikel.productfiche_id || '',
      artikel_omschrijving: artikel.naam,
      tekeningnummer: f.tekeningnummer,
      diameter_mm: artikel.diameter_mm || '',
      tanden_per_stuk: artikel.tanden_per_stuk || '',
      materiaal_beschrijving: artikel.standaard_materiaal || f.materiaal_beschrijving,
      materiaal_leverancier_id: artikel.standaard_leverancier_id || f.materiaal_leverancier_id,
    }));
    setTemplateBericht(`Route van artikel "${artikel.naam}" geladen (${artikel.route?.length || 0} stappen).`);
    setBewaarAlsArtikel(false);
    // Haal materiaalgeschiedenis op voor dit artikel
    try {
      const { data: mat } = await api.get(`/orders/artikel-materiaal?artikel_id=${id}`);
      setMateriaalSuggesties(mat);
    } catch { /* niet kritisch */ }
  };

  const laadFiche = async (id) => {
    stel('productfiche_id', id);
    if (!id) { setGeselecteerdeFiche(null); return; }
    const { data: fiche } = await api.get(`/productfiches/${id}`);
    setGeselecteerdeFiche(fiche);
    if (!form.artikel_id) {
      setRoute(fiche.route || []);
      setForm(f => ({
        ...f, productfiche_id: id,
        artikel_omschrijving: f.artikel_omschrijving || fiche.omschrijving,
        tekeningnummer: f.tekeningnummer || fiche.tekeningnummer || '',
        materiaal_beschrijving: f.materiaal_beschrijving || fiche.standaard_materiaal || '',
        materiaal_leverancier_id: f.materiaal_leverancier_id || fiche.standaard_leverancier_id || '',
      }));
      try {
        const { data: template } = await api.get(`/orders/materialen-template/${id}`);
        if (template.length > 0) {
          setMaterialen(template.map(m => ({ _id: Math.random(), naam: m.naam, referentie: m.referentie || '', leverancier_id: m.leverancier_id || '', eenheid: m.eenheid || 'stuks', tanden_per_stuk: m.tanden_per_stuk || '', vereist_voor_operaties: m.vereist_voor_operaties || [], status: 'te_bestellen', opmerkingen: m.opmerkingen || '' })));
          setTemplateBericht('Materialen overgenomen van vorig gelijkaardig order.');
        }
      } catch { /* geen template */ }
    }
  };

  // Klant-historiek artikel toepassen
  const pasKlantArtikelToe = async (ka) => {
    setForm(f => ({
      ...f,
      artikel_omschrijving: ka.artikel_omschrijving || f.artikel_omschrijving,
      tekeningnummer: ka.tekeningnummer || f.tekeningnummer,
      diameter_mm: ka.diameter_mm || f.diameter_mm,
      tanden_per_stuk: ka.tanden_per_stuk || f.tanden_per_stuk,
    }));
    if (ka.artikel_id) {
      await laadArtikel(ka.artikel_id);
    } else if (ka.productfiche_id) {
      await laadFiche(ka.productfiche_id);
    }
  };

  // Route helpers
  const voegStapToe = () => {
    const op = operaties[0]; if (!op) return;
    setRoute(r => [...r, { operatie_id: op.id, volgorde: r.length + 1, label: '', is_extern: false, externe_partij: '' }]);
  };
  const wijzigStap = (i, veld, val) => setRoute(r => r.map((s, j) => j === i ? { ...s, [veld]: val } : s));
  const verwijderStap = (i) => setRoute(r => r.filter((_, j) => j !== i));
  const verplaatsStap = (i, richting) => {
    const ni = i + richting;
    if (ni < 0 || ni >= route.length) return;
    setRoute(r => { const n = [...r]; [n[i], n[ni]] = [n[ni], n[i]]; return n; });
  };

  // Materialen helpers
  const updateMat = (id, veld, val) => setMaterialen(m => m.map(x => x._id === id ? { ...x, [veld]: val } : x));
  const toggleVereistVoor = (matId, opNr) => setMaterialen(m => m.map(x => {
    if (x._id !== matId) return x;
    const l = x.vereist_voor_operaties || [];
    return { ...x, vereist_voor_operaties: l.includes(opNr) ? l.filter(n => n !== opNr) : [...l, opNr] };
  }));

  const routeOpNummers = route.map(s => {
    const op = operaties.find(o => o.id === parseInt(s.operatie_id));
    return op ? { nummer: op.nummer, naam: s.label || op.naam } : null;
  }).filter(Boolean);

  const aantalInt = parseInt(form.aantal) || 1;

  const valideer = () => {
    const e = {};
    if (!form.klant.trim())               e.klant = 'Verplicht';
    if (!form.artikel_omschrijving.trim()) e.artikel_omschrijving = 'Verplicht';
    if (!form.leverdatum)                  e.leverdatum = 'Verplicht';
    if (!form.aantal || parseInt(form.aantal) < 1) e.aantal = 'Min. 1';
    setFouten(e);
    return Object.keys(e).length === 0;
  };

  const aanmaken = async () => {
    if (!valideer()) { window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    setBezig(true);
    try {
      const { data } = await api.post('/orders', {
        ...form,
        ordernummer: form.ordernummer.trim() || undefined,
        aantal: parseInt(form.aantal),
        artikel_id: form.artikel_id || null,
        productfiche_id: form.productfiche_id || null,
        materiaal_leverancier_id: form.materiaal_leverancier_id || null,
        diameter_mm: form.diameter_mm || null,
        tanden_per_stuk: form.tanden_per_stuk ? parseInt(form.tanden_per_stuk) : null,
        route: route.map((s, i) => ({ ...s, volgorde: i + 1, operatie_id: parseInt(s.operatie_id) })),
        materialen: materialen.filter(m => m.naam.trim()).map(m => ({
          naam: m.naam, referentie: m.referentie || null,
          leverancier_id: m.leverancier_id || null,
          eenheid: m.eenheid || 'stuks',
          tanden_per_stuk: m.tanden_per_stuk ? parseInt(m.tanden_per_stuk) : null,
          vereist_voor_operaties: m.vereist_voor_operaties?.length ? m.vereist_voor_operaties : null,
          status: m.status || 'te_bestellen',
          opmerkingen: m.opmerkingen || null,
        })),
      });

      // Bewaar nieuw artikel in bibliotheek indien aangevinkt
      if (bewaarAlsArtikel && !form.artikel_id) {
        try {
          await api.post('/artikelen', {
            naam: form.artikel_omschrijving,
            tekeningnummer: form.tekeningnummer || null,
            diameter_mm: form.diameter_mm || null,
            tanden_per_stuk: form.tanden_per_stuk ? parseInt(form.tanden_per_stuk) : null,
            productfiche_id: form.productfiche_id || null,
            standaard_materiaal: form.materiaal_beschrijving || null,
            standaard_leverancier_id: form.materiaal_leverancier_id || null,
            route: route.map((s, i) => ({ ...s, volgorde: i + 1, operatie_id: parseInt(s.operatie_id) })),
          });
        } catch (artErr) {
          console.warn('Artikel opslaan mislukt:', artErr.message);
          // Order is al aangemaakt — niet blokkeren
        }
      }

      nav(`/orders/${data.id}`);
    } catch (e) { alert('Fout: ' + (e.response?.data?.error || e.message)); }
    setBezig(false);
  };

  const inp = (veld) => ({
    style: fouten[veld] ? S.fout : S.input,
    value: form[veld],
    onChange: e => stel(veld, e.target.value),
  });

  const sel = (veld) => ({
    style: { ...S.input, ...(fouten[veld] ? { borderColor: '#E05040' } : {}) },
    value: form[veld],
    onChange: e => stel(veld, e.target.value),
  });

  return (
    <div style={{ padding: 24, maxWidth: 860 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <button onClick={() => nav('/orders')} style={{ background: 'none', border: 'none', color: '#185FA5', cursor: 'pointer', fontSize: 13 }}>← Terug</button>
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: '#1F3864' }}>Nieuw order</h1>
      </div>

      {/* ── 1. Artikel ──────────────────────────────────────────────────────── */}
      <div style={S.kaart}>
        <div style={S.secHdr}><SecNr n={1} /> Artikel <span style={{ fontWeight: 400, fontSize: 12, color: '#888' }}>(laadt route automatisch)</span></div>

        {/* Familie filter */}
        {families.length > 0 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
            <button onClick={() => setFamilieFilter('')} style={{ padding: '4px 10px', borderRadius: 99, fontSize: 11, cursor: 'pointer', border: '1px solid #DDD', background: !familieFilter ? '#1F3864' : '#fff', color: !familieFilter ? '#fff' : '#555' }}>Alle</button>
            {families.map(f => (
              <button key={f.id} onClick={() => setFamilieFilter(familieFilter === String(f.id) ? '' : String(f.id))} style={{
                padding: '4px 10px', borderRadius: 99, fontSize: 11, cursor: 'pointer',
                border: familieFilter === String(f.id) ? `1.5px solid ${f.kleur || '#185FA5'}` : '1px solid #DDD',
                background: familieFilter === String(f.id) ? (f.kleur || '#185FA5') + '20' : '#fff',
                color: familieFilter === String(f.id) ? (f.kleur || '#185FA5') : '#555',
                fontWeight: familieFilter === String(f.id) ? 600 : 400,
              }}>{f.naam}</button>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <input value={artikelZoek} onChange={e => setArtikelZoek(e.target.value)}
            placeholder="Zoeken op artikelnummer of naam..."
            style={{ flex: 1, ...S.input }} />
          {(familieFilter || artikelZoek) && (
            <button onClick={() => { setFamilieFilter(''); setArtikelZoek(''); }}
              style={{ padding: '8px 12px', border: '1px solid #DDD', borderRadius: 6, fontSize: 12, cursor: 'pointer', color: '#888', background: '#fff' }}>
              Wis filter
            </button>
          )}
        </div>

        <select value={form.artikel_id} onChange={e => laadArtikel(e.target.value)} style={S.input}>
          <option value="">— Nieuw/onbekend artikel (manueel route invullen) —</option>
          {gefilterdArtikel.map(a => (
            <option key={a.id} value={a.id}>
              {a.artikelnummer ? `${a.artikelnummer} — ` : ''}{a.naam}
              {a.diameter_mm ? ` · ⌀${parseFloat(a.diameter_mm)}mm` : ''}
              {a.tanden_per_stuk ? ` · ${a.tanden_per_stuk}t` : ''}
            </option>
          ))}
        </select>

        {gefilterdArtikel.length < artikelen.length && (
          <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>{gefilterdArtikel.length} van {artikelen.length} artikelen</div>
        )}

        {/* Artikel preview */}
        {geselecteerdArtikel && (
          <div style={{ marginTop: 12, padding: '10px 14px', background: '#F0F7FF', border: '1px solid #BDD4EE', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            {geselecteerdArtikel.familie_kleur && <div style={{ width: 6, height: 36, borderRadius: 3, background: geselecteerdArtikel.familie_kleur, flexShrink: 0 }} />}
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: '#0C447C' }}>{geselecteerdArtikel.naam}</div>
              <div style={{ fontSize: 11, color: '#555', marginTop: 2, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {geselecteerdArtikel.artikelnummer && <span>#{geselecteerdArtikel.artikelnummer}</span>}
                {geselecteerdArtikel.fiche_naam && <span>{geselecteerdArtikel.fiche_naam}</span>}
                {geselecteerdArtikel.diameter_mm && <span>⌀ {parseFloat(geselecteerdArtikel.diameter_mm)} mm</span>}
                {geselecteerdArtikel.tanden_per_stuk && <span>{geselecteerdArtikel.tanden_per_stuk} tanden/stuk</span>}
                {geselecteerdArtikel.route?.length > 0 && <span>✓ {geselecteerdArtikel.route.length} stappen geladen</span>}
              </div>
            </div>
            {templateBericht && (
              <div style={{ fontSize: 11, color: '#1A7A3C', background: '#EAF3DE', border: '1px solid #82C96B', borderRadius: 6, padding: '4px 10px' }}>
                {templateBericht}
              </div>
            )}
          </div>
        )}

        {/* Productfiche selector — altijd zichtbaar als geen artikel gekozen */}
        {!form.artikel_id && (
          <div style={{ marginTop: 14, padding: '12px 14px', background: '#F8F8F8', border: '1px solid #E5E5E5', borderRadius: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 8 }}>Productfiche als basis (optioneel)</div>
            <input value={ficheZoek} onChange={e => setFicheZoek(e.target.value)}
              placeholder="Zoeken op productfiche..."
              style={{ ...S.input, marginBottom: 6 }} />
            <select value={form.productfiche_id} onChange={e => laadFiche(e.target.value)} style={S.input}>
              <option value="">— Geen productfiche —</option>
              {gefilterdeFiches.map(f => (
                <option key={f.id} value={f.id}>
                  {f.artikelnummer ? `${f.artikelnummer} — ` : ''}{f.omschrijving}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Bewaar als nieuw artikel — alleen bij nieuw artikel */}
        {!form.artikel_id && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 13, color: '#444', cursor: 'pointer' }}>
            <input type="checkbox" checked={bewaarAlsArtikel} onChange={e => setBewaarAlsArtikel(e.target.checked)} />
            <span>Bewaar dit artikel in de artikelbibliotheek na aanmaken</span>
          </label>
        )}
      </div>

      {/* ── 2. Klant & artikel ──────────────────────────────────────────────── */}
      <div style={S.kaart}>
        <div style={S.secHdr}><SecNr n={2} /> Klant & artikel</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Veld label="Klant *" fout={fouten.klant}>
            <input {...inp('klant')} placeholder="Klantnaam" />
          </Veld>
          <Veld label="Referentie klant">
            <input {...inp('klant_referentie')} placeholder="PO-nummer, bestelbon..." />
          </Veld>

          {/* Historische artikelen voor deze klant */}
          {form.klant.trim() && klantArtikelen.length > 0 && (
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ ...S.label, color: '#185FA5' }}>
                Eerder gemaakt voor {form.klant.trim()} — kies als basis:
              </label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {klantArtikelen.map((ka, i) => (
                  <button key={i} type="button" onClick={() => pasKlantArtikelToe(ka)}
                    style={{
                      padding: '5px 12px', borderRadius: 99, fontSize: 12, cursor: 'pointer',
                      border: '1.5px solid #185FA5', background: '#EFF6FF', color: '#0C447C',
                      fontWeight: 500,
                    }}>
                    {ka.artikel_omschrijving}
                    {ka.diameter_mm ? ` · ⌀${parseFloat(ka.diameter_mm)}mm` : ''}
                    {ka.tanden_per_stuk ? ` · ${ka.tanden_per_stuk}t` : ''}
                  </button>
                ))}
              </div>
            </div>
          )}
          {form.klant.trim() && klantArtikelenLaden && (
            <div style={{ gridColumn: '1 / -1', fontSize: 12, color: '#888' }}>Artikelen laden...</div>
          )}

          <div style={{ gridColumn: '1 / -1' }}>
            <Veld label="Artikel / omschrijving *" fout={fouten.artikel_omschrijving}>
              <input {...inp('artikel_omschrijving')} placeholder="Productomschrijving" />
            </Veld>
          </div>
          <Veld label="Aantal stuks *" fout={fouten.aantal}>
            <input type="number" min="1" {...inp('aantal')} />
          </Veld>
          <Veld label="Tekeningnummer">
            <input {...inp('tekeningnummer')} placeholder="DWG-001" />
          </Veld>
          <WeekDatumPicker
            label="Startdatum"
            value={form.startdatum}
            onChange={v => stel('startdatum', v)}
          />
          <WeekDatumPicker
            label="Leverdatum *"
            fout={fouten.leverdatum}
            value={form.leverdatum}
            onChange={v => stel('leverdatum', v)}
            eindDatum
          />
          <Veld label="Prioriteit">
            <select {...sel('prioriteit')}>
              <option value="normaal">Normaal</option>
              <option value="hoog">Hoog</option>
              <option value="spoed">Spoed</option>
            </select>
          </Veld>
          <div style={{ gridColumn: '1 / -1' }}>
            <Veld label="Ordernummer (leeglaten = automatisch gegenereerd)">
              <input {...inp('ordernummer')} placeholder="bijv. ORD-2025-042 of ERP-referentie" />
            </Veld>
          </div>
        </div>
      </div>

      {/* ── 3. Hoofd-materiaal ──────────────────────────────────────────────── */}
      <div style={S.kaart}>
        <div style={S.secHdr}><SecNr n={3} /> Hoofd-materiaal</div>

        {/* Suggesties op basis van eerder gebruikt materiaal voor dit artikel */}
        {materiaalSuggesties.length > 0 && (
          <div style={{ background: '#F0FBF6', border: '1px solid #82C96B', borderRadius: 8, padding: '10px 14px', marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#267A5C', marginBottom: 8 }}>
              📦 Eerder gebruikt materiaal voor dit artikel:
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {materiaalSuggesties.map((s, i) => (
                <button key={i} type="button"
                  onClick={() => {
                    stel('materiaal_beschrijving', s.materiaal_naam || form.materiaal_beschrijving);
                    stel('materiaal_referentie', s.materiaal_referentie || form.materiaal_referentie);
                  }}
                  style={{
                    padding: '5px 12px', borderRadius: 99, fontSize: 12, cursor: 'pointer',
                    border: '1.5px solid #82C96B', background: '#EBF8F2', color: '#267A5C', fontWeight: 500,
                  }}>
                  {s.materiaal_naam}
                  {s.materiaal_referentie ? ` · ${s.materiaal_referentie}` : ''}
                  {s.materiaal_hoeveelheid ? ` · ${parseFloat(s.materiaal_hoeveelheid)}${s.materiaal_eenheid || ''}` : ''}
                  <span style={{ marginLeft: 6, fontSize: 10, color: '#888' }}>({s.keer_gebruikt}×)</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Veld label="Materiaalstatus">
            <select {...sel('materiaal_status')}>
              {Object.entries(MAT_STATUS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Veld>
          <Veld label="Leverancier">
            <select {...sel('materiaal_leverancier_id')}>
              <option value="">— Kies leverancier —</option>
              {leveranciers.map(l => <option key={l.id} value={l.id}>{l.naam}</option>)}
            </select>
          </Veld>
          <Veld label="Omschrijving">
            <input {...inp('materiaal_beschrijving')} placeholder="Staaltype, afmeting, kwaliteit..." />
          </Veld>
          <Veld label="Referentie">
            <input {...inp('materiaal_referentie')} placeholder="Artikelnummer leverancier" />
          </Veld>
        </div>
      </div>

      {/* ── 4. Routekaart ───────────────────────────────────────────────────── */}
      <div style={S.kaart}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={S.secHdr}><SecNr n={4} /> Routekaart <span style={{ fontWeight: 400, fontSize: 12, color: '#888' }}>({route.length} stappen)</span></div>
          <button onClick={voegStapToe} style={{ padding: '6px 14px', background: '#185FA5', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>+ Stap</button>
        </div>

        {route.length === 0 && (
          <div style={{ color: '#AAA', fontSize: 13, fontStyle: 'italic' }}>Geen stappen — kies een productfiche of voeg manueel stappen toe.</div>
        )}

        {route.map((stap, i) => {
          const opNummer = operaties.find(o => o.id === parseInt(stap.operatie_id))?.nummer;
          return (
            <div key={i} style={{ background: '#F7F9FC', border: '1px solid #E8E8E8', borderRadius: 8, padding: '10px 12px', marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: stap.is_extern ? 8 : 0 }}>
                <span style={{ fontSize: 11, color: '#AAA', minWidth: 20, textAlign: 'right' }}>{i + 1}</span>
                <select value={stap.operatie_id}
                  onChange={e => wijzigStap(i, 'operatie_id', parseInt(e.target.value))}
                  style={{ flex: 1, padding: '5px 8px', border: '1px solid #DDD', borderRadius: 6, fontSize: 12 }}>
                  {operaties.map(o => <option key={o.id} value={o.id}>{o.nummer}. {o.naam}</option>)}
                </select>
                <input placeholder="Label (optioneel)" value={stap.label || ''}
                  onChange={e => wijzigStap(i, 'label', e.target.value)}
                  style={{ width: 150, padding: '5px 8px', border: '1px solid #DDD', borderRadius: 6, fontSize: 12 }} />
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#555', whiteSpace: 'nowrap', cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!stap.is_extern} onChange={e => wijzigStap(i, 'is_extern', e.target.checked)} />
                  Extern
                </label>
                <div style={{ display: 'flex', gap: 2 }}>
                  <button onClick={() => verplaatsStap(i, -1)} disabled={i === 0}
                    style={{ background: 'none', border: '1px solid #DDD', borderRadius: 4, padding: '2px 6px', cursor: i === 0 ? 'default' : 'pointer', color: i === 0 ? '#CCC' : '#555', fontSize: 11 }}>↑</button>
                  <button onClick={() => verplaatsStap(i, 1)} disabled={i === route.length - 1}
                    style={{ background: 'none', border: '1px solid #DDD', borderRadius: 4, padding: '2px 6px', cursor: i === route.length - 1 ? 'default' : 'pointer', color: i === route.length - 1 ? '#CCC' : '#555', fontSize: 11 }}>↓</button>
                </div>
                <button onClick={() => verwijderStap(i)} style={{ background: 'none', border: 'none', color: '#C0392B', cursor: 'pointer', fontSize: 20, lineHeight: 1, padding: '0 4px' }}>×</button>
              </div>
              {stap.is_extern && (
                <div style={{ marginLeft: 28, marginTop: 4 }}>
                  <input placeholder="Externe partij (bijv. Ventec, Bodycote...)" value={stap.externe_partij || ''}
                    onChange={e => wijzigStap(i, 'externe_partij', e.target.value)}
                    style={{ width: '100%', padding: '5px 8px', border: '1px solid #F0C060', borderRadius: 6, fontSize: 12, background: '#FFFBF0', boxSizing: 'border-box' }} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── 5. Extra materialen ─────────────────────────────────────────────── */}
      <div style={S.kaart}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={S.secHdr}>
            <SecNr n={5} /> Extra materialen
            <span style={{ fontWeight: 400, fontSize: 12, color: '#888' }}>tanden, platen, onderdelen per stap</span>
          </div>
          <button onClick={() => setMaterialen(m => [...m, leegMat()])}
            style={{ padding: '6px 14px', background: '#185FA5', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>
            + Toevoegen
          </button>
        </div>

        {materialen.length === 0 && (
          <div style={{ color: '#AAA', fontSize: 13, fontStyle: 'italic' }}>Geen extra materialen toegevoegd.</div>
        )}

        {materialen.map(mat => {
          const totaalTanden = mat.tanden_per_stuk ? parseInt(mat.tanden_per_stuk) * aantalInt : null;
          return (
            <div key={mat._id} style={{ background: '#FAFAFA', border: '1px solid #E8E8E8', borderRadius: 8, padding: 14, marginBottom: 10 }}>
              {/* Rij 1: naam, ref, lev, verwijder */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 150px 180px auto', gap: 10, marginBottom: 10, alignItems: 'end' }}>
                <Veld label="Naam *">
                  <input style={S.input} placeholder="bijv. HM-tanden, staalplaat..." value={mat.naam} onChange={e => updateMat(mat._id, 'naam', e.target.value)} />
                </Veld>
                <Veld label="Referentie">
                  <input style={S.input} placeholder="Artikelnr." value={mat.referentie} onChange={e => updateMat(mat._id, 'referentie', e.target.value)} />
                </Veld>
                <Veld label="Leverancier">
                  <select style={S.input} value={mat.leverancier_id} onChange={e => updateMat(mat._id, 'leverancier_id', e.target.value)}>
                    <option value="">— geen —</option>
                    {leveranciers.map(l => <option key={l.id} value={l.id}>{l.naam}</option>)}
                  </select>
                </Veld>
                <button onClick={() => setMaterialen(m => m.filter(x => x._id !== mat._id))}
                  style={{ background: 'none', border: 'none', color: '#C0392B', cursor: 'pointer', fontSize: 22, lineHeight: 1, padding: '4px', alignSelf: 'center' }}>×</button>
              </div>

              {/* Tanden-berekening */}
              <div style={{ background: '#FFFBF0', border: '1px solid #F0C060', borderRadius: 6, padding: '8px 12px', marginBottom: 10 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#8A5C00', marginBottom: mat.tanden_per_stuk !== '' ? 8 : 0, cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!mat.tanden_per_stuk}
                    onChange={e => updateMat(mat._id, 'tanden_per_stuk', e.target.checked ? '1' : '')} />
                  Tanden-berekening (HM-tanden, zaagbladinzet...)
                </label>
                {mat.tanden_per_stuk !== '' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <input type="number" min="1" value={mat.tanden_per_stuk}
                      onChange={e => updateMat(mat._id, 'tanden_per_stuk', e.target.value)}
                      style={{ width: 90, padding: '5px 8px', border: '1px solid #DDD', borderRadius: 6, fontSize: 13 }}
                      placeholder="Tanden/stuk" />
                    <span style={{ fontSize: 13, color: '#888' }}>× {aantalInt} stuks =</span>
                    <span style={{ fontSize: 16, fontWeight: 700, color: '#8A5C00' }}>
                      {totaalTanden ? `${totaalTanden} tanden` : '?'}
                    </span>
                  </div>
                )}
              </div>

              {/* Rij 2: status, vereist voor */}
              <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <div>
                  <label style={S.label}>Status</label>
                  <select style={{ ...S.input, width: 'auto' }} value={mat.status} onChange={e => updateMat(mat._id, 'status', e.target.value)}>
                    {Object.entries(MAT_STATUS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                {routeOpNummers.length > 0 && (
                  <div style={{ flex: 1 }}>
                    <label style={S.label}>Vereist voor stap (blokkeert activering)</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {routeOpNummers.map(op => {
                        const sel = (mat.vereist_voor_operaties || []).includes(op.nummer);
                        return (
                          <button key={op.nummer} type="button" onClick={() => toggleVereistVoor(mat._id, op.nummer)} style={{
                            padding: '3px 10px', borderRadius: 99, fontSize: 12, cursor: 'pointer',
                            border: sel ? '1.5px solid #185FA5' : '1px solid #DDD',
                            background: sel ? '#E6F1FB' : '#fff',
                            color: sel ? '#0C447C' : '#666',
                            fontWeight: sel ? 600 : 400,
                          }}>{op.naam}</button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── 6. Opmerkingen ──────────────────────────────────────────────────── */}
      <div style={S.kaart}>
        <div style={S.secHdr}><SecNr n={6} /> Opmerkingen & uitbesteding</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={S.label}>Opmerkingen</label>
            <textarea value={form.opmerkingen} onChange={e => stel('opmerkingen', e.target.value)}
              rows={3} placeholder="Speciale instructies, afspraken..."
              style={{ ...S.input, resize: 'vertical' }} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={S.label}>Uitbesteding info</label>
            <input value={form.uitbesteding_info} onChange={e => stel('uitbesteding_info', e.target.value)}
              placeholder="bijv. Harden bij Ventec — week 23"
              style={S.input} />
          </div>
        </div>
      </div>

      {/* Acties */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 40 }}>
        <button onClick={aanmaken} disabled={bezig} style={{
          padding: '11px 28px', background: '#1A7A3C', color: '#fff', border: 'none',
          borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: bezig ? 'wait' : 'pointer',
        }}>
          {bezig ? 'Aanmaken...' : 'Order aanmaken'}
        </button>
        <button onClick={() => nav('/orders')} style={{
          padding: '11px 20px', background: '#fff', color: '#555',
          border: '1px solid #DDD', borderRadius: 8, fontSize: 14, cursor: 'pointer',
        }}>
          Annuleren
        </button>
        {Object.keys(fouten).length > 0 && (
          <span style={{ fontSize: 13, color: '#C0392B' }}>
            Controleer de verplichte velden ({Object.keys(fouten).length})
          </span>
        )}
      </div>
    </div>
  );
}
